import { registryFormIdFromSlotId } from './registryFormIdFromSlotId'
import { resolveElementRegistryFormId } from './resolveElementRegistryFormId'

export interface PageIdentity {
  slotId: string
  registryFormId: string
  windowInstanceId: string
}

interface ResolvePageIdentityOptions {
  slotId?: string | number | null
  registryFormId?: string | number | null
  windowInstanceId?: string | number | null
}

const normalizeStructuredStringId = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed || trimmed.startsWith('[object')) return ''
  if (trimmed.includes('typeId:') || trimmed.includes('document:') || trimmed.includes('view:')) {
    const match = trimmed.match(/(?:^|[\s,(;])(?:fid|id)\s*:\s*['"]?([^'",)\s]+)['"]?/i)
    return match?.[1]?.trim() || ''
  }
  return trimmed
}

const normalizeId = (value: unknown, depth = 0): string => {
  if (depth > 3) return ''
  if (value == null) return ''
  if (typeof value === 'string') return normalizeStructuredStringId(value)
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const o = value as Record<string, unknown>
    for (const candidate of [o.fid, o.id, o._id, o._text, o.value, o.formId, o.view, o.document]) {
      const normalized = normalizeId(candidate, depth + 1)
      if (normalized) return normalized
    }
  }
  return ''
}

const resolveEmbeddedWindowInstanceId = (pageData: Record<string, any> | null | undefined): string => {
  if (!pageData || typeof pageData !== 'object') return ''
  const stackHead = Array.isArray(pageData.stack) ? pageData.stack[0] : null
  return normalizeId(stackHead)
}

export function resolveFormSchemaIdFromPageEntry(
  pageData: Record<string, unknown> | null | undefined,
  fallbackSlotOrActionId?: unknown,
): number {
  const tryPositiveInt = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value
    }
    if (typeof value === 'string') {
      const n = parseInt(value.trim(), 10)
      if (Number.isFinite(n) && n > 0) return n
    }
    return null
  }

  if (pageData && typeof pageData === 'object') {
    const pd = pageData as Record<string, unknown>
    const jsResult = pd.jsResult as Record<string, unknown> | undefined
    const formRoot = pd.form as Record<string, unknown> | undefined
    const formInner = formRoot?.form as Record<string, unknown> | undefined
    const formAttrs = (formInner?._attributes ?? formRoot?._attributes) as
      | Record<string, unknown>
      | undefined

    for (const candidate of [
      jsResult?.form_id,
      formAttrs?.id,
      formAttrs?.fid,
    ]) {
      const n = tryPositiveInt(candidate)
      if (n != null) return n
    }
  }

  const slot = String(fallbackSlotOrActionId ?? '').trim()
  const fromSlot = tryPositiveInt(registryFormIdFromSlotId(slot))
  if (fromSlot != null) return fromSlot

  return tryPositiveInt(fallbackSlotOrActionId) ?? 0
}

export function resolvePageIdentity(
  pageData: Record<string, any> | null | undefined,
  options: ResolvePageIdentityOptions = {},
): PageIdentity {
  const slotId = normalizeId(options.slotId)
  const registryFormId =
    normalizeId(options.registryFormId) ||
    resolveElementRegistryFormId(pageData, slotId) ||
    slotId
  const windowInstanceId =
    normalizeId(options.windowInstanceId) ||
    resolveEmbeddedWindowInstanceId(pageData) ||
    slotId

  return {
    slotId,
    registryFormId,
    windowInstanceId,
  }
}

export function getPageIdentityAliases(
  pageData: Record<string, any> | null | undefined,
  options: ResolvePageIdentityOptions = {},
): string[] {
  const identity = resolvePageIdentity(pageData, options)
  const aliases = new Set<string>()

  const add = (value: unknown) => {
    const normalized = normalizeId(value)
    if (normalized) aliases.add(normalized)
  }

  add(identity.slotId)
  add(identity.registryFormId)
  add(identity.windowInstanceId)

  if (pageData && typeof pageData === 'object') {
    add(pageData.status)
    add(pageData.$fid)
    add(pageData.formId)
    add(pageData.jsResult?.form_id)
    add(pageData.form?.form?._attributes?.id)
    add(pageData.form?.form?._attributes?.fid)
    add(pageData.form?._attributes?.id)
    add(pageData.form?._attributes?.fid)
    add(Array.isArray(pageData.stack) ? pageData.stack[0] : null)
  }

  return [...aliases]
}
